import React, { useEffect, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { format, subDays, subMonths, subYears } from 'date-fns';
import { Users, Key, AlertTriangle, Clock } from 'lucide-react';
import {
  Button,
  Card,
  PageHeader,
  Alert,
} from '@license-management/ui';
import { analyticsService, type UsageTrends, type CustomerMetric } from '../api/services/analyticsService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface SystemHealth {
  activeCustomers: number;
  activeLicenses: number;
  totalActivations: number;
  errorRate: number;
  avgResponseTime: number | null;
  lastUpdated: string;
}

export const Analytics: React.FC = () => {
  const [timeframe, setTimeframe] = useState('week');
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [usageTrends, setUsageTrends] = useState<UsageTrends | null>(null);
  const [customerMetrics, setCustomerMetrics] = useState<CustomerMetric[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getDateRange = () => {
    const endDate = format(new Date(), 'yyyy-MM-dd');
    let startDate;

    switch (timeframe) {
      case 'week':
        startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd');
        break;
      case 'month':
        startDate = format(subMonths(new Date(), 1), 'yyyy-MM-dd');
        break;
      case 'year':
        startDate = format(subYears(new Date(), 1), 'yyyy-MM-dd');
        break;
      default:
        startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd');
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        const range = getDateRange();
        const [health, trends, customers] = await Promise.all([
          analyticsService.getSystemHealth(),
          analyticsService.getUsageTrends(range),
          analyticsService.getCustomerMetrics(range),
        ]);

        setSystemHealth({
          activeCustomers: customers.summary.active || 0,
          activeLicenses: health.activeLicenses || 0,
          totalActivations: health.totalActivations || 0,
          errorRate: health.errorRate || 0,
          avgResponseTime: health.avgResponseTime || 0,
          // The backend response never includes a `lastUpdated` field (see
          // analyticsService.ts), so this fallback was always taken.
          lastUpdated: new Date().toISOString()
        });

        setUsageTrends(trends);
        setCustomerMetrics(customers.byType || []);
        setError(null);
      } catch (err) {
        console.error('Analytics error:', err);
        setError(err instanceof Error ? err.message : 'An error occurred while fetching analytics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [timeframe]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-zinc-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto mt-8">
        <Alert variant="error">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="font-semibold">Error loading analytics</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        </Alert>
      </div>
    );
  }

  const activeUsersData = {
    labels: usageTrends?.activations.map(d => d.period),
    datasets: [
      {
        label: 'Active Users',
        data: usageTrends?.activations.map(d => d.count),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        tension: 0.3,
      },
    ],
  };

  const validationData = {
    labels: usageTrends?.customers.map(d => d.period),
    datasets: [
      {
        label: 'Success Rate (%)',
        data: usageTrends?.customers.map(d => d.count),
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderColor: 'rgb(34, 197, 94)',
        tension: 0.3,
      },
    ],
  };

  const responseTimeData = {
    labels: usageTrends?.licenses.map(d => d.period),
    datasets: [
      {
        label: 'Response Time (ms)',
        data: usageTrends?.licenses.map(d => d.count),
        backgroundColor: 'rgba(239, 68, 68, 0.5)',
        borderColor: 'rgb(239, 68, 68)',
        tension: 0.3,
      },
    ],
  };

  const customerData = {
    labels: customerMetrics.slice(0, 10).map(c => c.company),
    datasets: [
      {
        label: 'Active Users',
        data: customerMetrics.slice(0, 10).map(c => c.activeUsers),
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
      },
      {
        label: 'Active Licenses',
        data: customerMetrics.slice(0, 10).map(c => c.activeLicenses),
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
      },
    ],
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="License Analytics"
        description="Monitor license usage, system health, and customer activity"
        actions={
          <div className="flex gap-2">
            {['week', 'month', 'year'].map((period) => (
              <Button
                key={period}
                variant={timeframe === period ? 'primary' : 'secondary'}
                onClick={() => setTimeframe(period)}
                size="sm"
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </Button>
            ))}
          </div>
        }
      />

      {/* System Health Cards */}
      {systemHealth && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary-100 text-primary-600">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-500">Active Customers</p>
                <p className="text-2xl font-semibold text-zinc-900">{systemHealth.activeCustomers}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <Key className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-500">Active Licenses</p>
                <p className="text-2xl font-semibold text-zinc-900">{systemHealth?.activeLicenses || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-500">Error Rate</p>
                <p className="text-2xl font-semibold text-zinc-900">{(systemHealth?.errorRate || 0).toFixed(2)}%</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-100 text-cyan-600">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-500">Avg Response Time</p>
                <p className="text-2xl font-semibold text-zinc-900">{(systemHealth?.avgResponseTime || 0).toFixed(2)}ms</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Active Users</h3>
          <Line data={activeUsersData} options={{ responsive: true }} />
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Validation Success Rate</h3>
          <Line data={validationData} options={{
            responsive: true,
            scales: {
              y: {
                min: 0,
                max: 100,
                title: {
                  display: true,
                  text: 'Success Rate (%)'
                }
              }
            }
          }} />
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Response Time</h3>
          <Line data={responseTimeData} options={{
            responsive: true,
            scales: {
              y: {
                title: {
                  display: true,
                  text: 'Response Time (ms)'
                }
              }
            }
          }} />
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Top 10 Customers</h3>
          <Bar data={customerData} options={{
            responsive: true,
            plugins: {
              legend: {
                position: 'top',
              },
            }
          }} />
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
